import PageLayout from '@/components/layout/PageLayout';

/**
 * PrivacyPage Component
 * Comprehensive privacy policy for Notematic
 * Complies with Privacy Act 1988 (Cth), APPs, and NDB Scheme
 */
export default function PrivacyPage() {
  return (
    <PageLayout>
      <div className="section-padding bg-white dark:bg-secondary-900">
        <div className="container-custom max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-8">
            Notematic – Privacy Policy
          </h1>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-lg text-secondary-600 dark:text-secondary-400 mb-8">
              Last updated: 11 March 2026
            </p>
            <p className="text-secondary-700 dark:text-secondary-300 mb-8 text-lg leading-relaxed">
              At Notematic, we respect your privacy and are committed to protecting your personal and health information.
              This Privacy Policy explains how we collect, use, disclose, and safeguard information when you use our
              application and related services.
            </p>
            <p className="text-secondary-700 dark:text-secondary-300 mb-12 text-lg leading-relaxed">
              We comply with the <strong>Privacy Act 1988 (Cth)</strong>, the <strong>Australian Privacy Principles (APPs)</strong>,
              and the <strong>Notifiable Data Breaches (NDB) Scheme</strong>.
            </p>

            {/* System Role Section */}
            <section className="mb-12 bg-primary-50 dark:bg-primary-950 border-l-4 border-primary-600 p-6 rounded-r-lg">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6">
                Important: Notematic's Role as a Processing Tool
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                <strong>Notematic is a clinical documentation assistance and data processing platform.</strong> It is important to understand:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-3">
                <li><strong>Notematic is NOT a clinical record-keeping system</strong> and does not function as the system of record for patient health information.</li>
                <li><strong>Your clinic's EMR/CRM system is the authoritative repository</strong> for all clinical records and is solely responsible for statutory health record retention obligations.</li>
                <li><strong>Notematic retains temporary working copies only</strong> during the processing lifecycle to generate draft notes, support clinician review, and export finalized notes to your EMR/CRM.</li>
                <li><strong>All clinical documentation must be exported</strong> to your clinic's system of record before deletion from Notematic.</li>
                <li><strong>After export and verification periods, Notematic deletes</strong> transcripts and draft notes in accordance with this policy. Audio is not stored by Notematic — it is processed transiently in-memory only and discarded immediately after transcription.</li>
                <li><strong>After deletion, only the following are retained:</strong> consent records (7 years, Health Records Act 2001), compliance evidence logs (audit trails with no clinical content), and non-identifying structured metrics.</li>
              </ul>
            </section>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                1. Information We Collect
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                We may collect the following categories of information:
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                a) Personal Information
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Your name, email address, and contact details.</li>
                <li>Account credentials and authentication information.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                b) Sensitive Information (Health Information)
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Audio of client sessions — processed transiently in-memory for transcription only; not stored persistently. Audio is discarded immediately after transcription completes.</li>
                <li>Transcripts and structured notes derived from those recordings.</li>
                <li>Information relating to the Initial Assessment and Referral (IAR) 8 domains, such as presenting problems, risk factors, family and medical history.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                c) Technical Information
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Device identifiers, app version, operating system details.</li>
                <li>Log data for security and audit purposes.</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                2. How We Collect Information
              </h2>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Directly from you, when you use the app or provide consent.</li>
                <li>From recordings and transcripts generated within the app.</li>
                <li>From your device (technical logs, session identifiers).</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mt-6">
                We will only collect health information with your consent, or where required or authorised by law.
              </p>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                3. Purpose of Collection and Use
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                We collect and use information to:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Provide transcription and structured note drafting aligned to the IAR 8 domains.</li>
                <li>Enable clinicians to review, edit, and finalise assessment notes.</li>
                <li>Improve accuracy, security, and functionality of the service.</li>
                <li>Meet legal and compliance requirements.</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mt-6">
                We will not use your information for any secondary purpose without your consent, unless permitted by law.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                4. Use of Microsoft Azure AI Services
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic uses <strong>Microsoft Azure AI services</strong> to provide transcription and AI-assisted draft generation:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Azure Speech Service:</strong> Speech-to-text transcription of session recordings</li>
                <li><strong>Azure OpenAI Service:</strong> AI-assisted draft generation and clinical note structuring</li>
                <li><strong>Natural Language Processing:</strong> Clinical note structuring and template formatting</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Data Residency and Security
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Australian Data Residency:</strong> Azure AI services are hosted in Australia (Australia East region) to ensure data residency compliance.</li>
                <li><strong>Sub-Processor Relationship:</strong> Microsoft acts as a sub-processor under contract and Data Processing Agreement (DPA).</li>
                <li><strong>No Training on Customer Data:</strong> Microsoft's Azure OpenAI Service operates under a commitment that customer prompts and completions are NOT used to train, retrain, or improve Microsoft foundation models. Customer data is NOT shared with OpenAI and is processed solely for your use.</li>
                <li><strong>Compliance Standards:</strong> Microsoft Azure AI services comply with Australian Privacy Act 1988, ISO 27001, SOC 2, and other relevant standards.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Notematic Does NOT Train on Your Clinical Data
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic does <strong>NOT</strong> train AI models on:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Identifiable client data</li>
                <li>Session transcripts (verbatim conversations)</li>
                <li>Clinician-edited clinical text or draft content</li>
                <li>Any personal or health information</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mt-4">
                Notematic may improve note generation quality over time using <strong>only structured edit signals</strong> (non-identifying, aggregated statistical patterns such as edit categories, counts, and metrics). This approach ensures continuous improvement while maintaining strict privacy-by-design principles.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                5. Data Storage Layers and Deletion Coverage
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Personal and health information may temporarily exist in the following storage layers during processing. All layers are covered by our deletion policy:
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Storage Layers
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-3">
                <li><strong>Primary Database (Azure SQL):</strong> Structured data including sessions, drafts, transcripts, consents, and audit logs. Hard deletion applies to all records after soft-delete grace period.</li>
                <li><strong>Blob Storage (Azure Blob Storage):</strong> Export files and attachments. Files are deleted when parent export records are hard-deleted. Audio is NOT stored in Blob Storage — audio is processed entirely in-memory and never written to any persistent storage layer.</li>
                <li><strong>Search Indexes:</strong> Full-text search indexes (if implemented) are automatically updated when records are deleted. No standalone retention of deleted content.</li>
                <li><strong>Cache (Redis/In-Memory):</strong> Performance optimization caching with automatic TTL (Time To Live) expiry. Cache invalidation triggered on entity deletion.</li>
                <li><strong>Message Queues (Hangfire):</strong> Background job queues for processing. Messages are transient and processed within minutes with no long-term retention.</li>
                <li><strong>Backups:</strong> Database backups retained for maximum 30 days. Backup restoration procedures exclude soft-deleted records.</li>
                <li><strong>Logs and Monitoring:</strong> Application logs and monitoring data contain NO clinical content—only operational metadata (entity IDs, timestamps, actions).</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Deletion Propagation
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                When data is deleted from Notematic, deletion propagates across all storage layers:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Database records are permanently removed</li>
                <li>Blob storage export files are deleted</li>
                <li>Search indexes are updated</li>
                <li>Cache entries are invalidated</li>
                <li>Backups older than 30 days are purged</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mt-4">
                Automated verification jobs run weekly to ensure no orphaned data remains across any storage layer.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                6. Storage and Data Security
              </h2>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>All data is stored securely in Australian data centres (Australia East region).</li>
                <li>Transcripts and export files are encrypted in transit (TLS 1.2+) and at rest (AES-256). Audio is never stored — it is processed in-memory only, encrypted in transit via TLS 1.2+.</li>
                <li>Access is restricted to authorised users (clinicians and system administrators under strict role-based controls).</li>
                <li>Audit logs are maintained to track access, export, and deletion activities.</li>
                <li>Multi-factor authentication (MFA) required for all clinician accounts.</li>
                <li>Regular security audits and penetration testing conducted.</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                7. Sharing of Information
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                We will never sell or trade your information. We may disclose your information to:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Clinicians involved in your care, as authorised.</li>
                <li>Service providers (e.g., Microsoft Azure for secure cloud hosting and AI services) who are bound by agreements requiring compliance with Australian privacy law.</li>
                <li>Regulatory or law enforcement authorities where required by law.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                8. Consent
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                Notematic operates a <strong>two-tier consent model</strong> to ensure lawful processing at both the
                organisational and session level.
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Tier 1 — Clinician Organisational Consent (One-Time, at Onboarding)
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                When a clinician accepts an invitation to join a practice, they provide a single, durable
                <strong> Organisational Consent</strong> through the Web App. Clinicians must read and explicitly accept
                all 12 obligations before their account is activated, including:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-4 space-y-2">
                <li>Acknowledgment that Notematic is a processing tool and not a system of record</li>
                <li>Agreement to the Export-Only Data Access Policy</li>
                <li>Understanding of the 7-year data retention obligations (session data from session date; consent record from account closure date)</li>
                <li>Acceptance of the Privacy Policy and Terms of Service</li>
                <li>Acknowledgment of professional standards (AHPRA), audio processing boundaries, and all-party consent obligations</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                This consent is captured once and remains in effect for the life of the clinician's account.
                It is <strong>non-withdrawable</strong> and retained for a minimum of 7 years after account closure
                (Health Records Act 2001).
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Tier 2 — Client Session Consent (Per Session)
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Before any recording begins, the app requires explicit consent from all client participants.
                Session consent covers four scopes: Recording, Transcription, Draft note generation, and Export.
                Consent will be:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Captured digitally (name, date, purpose, jurisdiction).</li>
                <li>Stored securely with the session record.</li>
                <li>Withdrawable at any time (recordings and transcripts will then be deleted, unless required for clinical or legal reasons).</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                9. Retention and Deletion
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                We retain different types of data for varying periods based on clinical, legal, and compliance requirements:
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Data Retention Periods
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Audio Recordings:</strong> Not retained. Audio is processed entirely in-memory during the session and discarded immediately after transcription. No audio files are stored by Notematic.</li>
                <li><strong>Transcripts:</strong> Retained until clinician review; Maximum 30 days post-finalization. Automatically deleted thereafter.</li>
                <li><strong>Draft Notes:</strong> Retained until clinician approves and exports; Deleted within 24 hours after export to your EMR/CRM.</li>
                <li><strong>Exported Notes:</strong> Exported to your clinic's EMR/CRM system (system of record). Notematic deletes all copies within 24 hours of export.</li>
                <li><strong>Client Session Consent Records:</strong> Retained for 7 years from the date of the session to meet legal and professional obligations (Health Records Act 2001).</li>
                <li><strong>Clinician Organisational Consent Records:</strong> Retained for the duration of the clinician's active account plus 7 years after account closure or removal (Health Records Act 2001). Non-withdrawable.</li>
                <li><strong>Audit Logs:</strong> Retained for 7 years in immutable storage. Audit logs contain NO clinical content—only operational metadata.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Two-Stage Deletion Process
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Notematic implements a two-stage deletion process to balance legal compliance with operational safety:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>Stage 1 - Immediate Soft-Delete:</strong> Records are hidden from normal queries and marked for deletion. Data remains recoverable for 30 days to protect against accidental deletion.</li>
                <li><strong>Stage 2 - Scheduled Hard-Delete:</strong> After 30 days, records are permanently and irreversibly deleted from all storage layers (database, blob storage, cache, backups).</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Deletion Options
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li><strong>Automatic Deletion:</strong> Daily scheduled jobs automatically delete expired data according to the retention periods above.</li>
                <li><strong>Manual Deletion:</strong> Clinicians and Admins can delete transcripts or drafts directly through the application at any time (immediate soft-delete, permanently deleted after 30 days). Audio is never stored persistently and therefore cannot be manually deleted — it is discarded immediately after transcription.</li>
                <li><strong>Client Requests:</strong> Clients may request deletion of their data. Requests are processed immediately (soft-delete) with permanent deletion after 30 days, subject to professional and legal obligations.</li>
              </ul>

              <p className="text-secondary-700 dark:text-secondary-300 mt-6">
                <strong>Note:</strong> Data subject to legal hold (investigation or litigation) is excluded from automatic deletion until the hold is lifted.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                10. Your Rights: Access, Correction, and Deletion Requests
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                Under the Australian Privacy Principles (APPs), you have the following rights:
              </p>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Right to Access (APP 12)
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Request access to the personal information we hold about you</li>
                <li>Receive a copy of your data in a commonly used format</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Right to Correction (APP 13)
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Request correction of inaccurate, incomplete, or out-of-date personal information</li>
                <li>Request amendment of misleading information</li>
                <li>We will take reasonable steps to correct information upon request</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Deletion Requests (APP 11.2)
              </h3>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                You may request deletion of your personal information when it is no longer needed for a lawful purpose:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li><strong>During Active Session:</strong> You can request immediate session cancellation. All session data (transcript, draft) will be immediately soft-deleted and permanently deleted after 30 days. Audio is not stored — it is discarded immediately during in-memory processing.</li>
                <li><strong>After Draft Finalization:</strong> Once a draft is exported to your clinic's EMR/CRM, Notematic automatically deletes its copy within 24 hours. To delete the permanent record, you must request deletion from your clinic's EMR/CRM (the system of record).</li>
                <li><strong>Client Session Consent Records:</strong> Cannot be deleted for 7 years from the session date due to legal retention requirements under the Health Records Act 2001.</li>
                <li><strong>Clinician Organisational Consent Records:</strong> Cannot be deleted during the clinician's active account plus 7 years after account closure. These records are non-withdrawable proof of lawful data processing.</li>
              </ul>

              <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">
                Deletion Requests Subject To
              </h3>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Legal retention obligations (e.g., 7-year consent retention)</li>
                <li>Active legal or regulatory holds</li>
                <li>Clinical governance requirements of your clinic</li>
                <li>System of record responsibilities (your clinic's EMR/CRM)</li>
              </ul>

              <p className="text-secondary-700 dark:text-secondary-300 mt-6">
                <strong>Processing Timeline:</strong> Deletion requests are processed immediately (soft-delete) with permanent deletion within 30 days. You will receive a confirmation email upon completion.
              </p>

              <p className="text-secondary-700 dark:text-secondary-300 mt-6">
                To exercise any of these rights, please contact us (see Section 15).
              </p>
            </section>

            {/* Section 11 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                11. Minors and Special Retention Considerations
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                <strong>Statutory Record Retention for Minors:</strong>
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-3">
                <li><strong>Clinic's Responsibility:</strong> Where clients are minors (under 18 years), statutory record retention requirements (often until age 25 or longer) are the responsibility of the clinic's system of record (EMR/CRM). Notematic does not assume these obligations as it is not a clinical record system.</li>
                <li><strong>Notematic Processing Retention:</strong> Notematic applies standard processing retention periods regardless of client age. All clinical documentation is exported to the clinic's EMR/CRM prior to deletion.</li>
                <li><strong>Clinic's Obligations:</strong> Clinics are responsible for ensuring exported records comply with applicable child and youth record retention laws in their jurisdiction.</li>
              </ul>

              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                <strong>Consent for Minors and Vulnerable Individuals:</strong>
              </p>
              <p className="text-secondary-700 dark:text-secondary-300">
                Where clients are under 18 or are considered vulnerable, consent must also be obtained from a parent,
                guardian, or authorised representative before recording or storing sensitive information.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                12. Notifiable Data Breaches
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                If a data breach occurs that is likely to result in serious harm, we will:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 space-y-2">
                <li>Conduct an assessment within 30 days.</li>
                <li>Notify the Office of the Australian Information Commissioner (OAIC).</li>
                <li>Notify affected individuals with details of the breach and steps taken.</li>
              </ul>
            </section>

            {/* Section 13 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                13. International Data Disclosure
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300">
                We do not transfer your data outside Australia. All storage and processing is conducted within Australian jurisdiction (Australia East region).
              </p>
            </section>

            {/* Section 14 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                14. Legal and Regulatory Holds
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-4">
                Deletion processes may be suspended where required by law, including:
              </p>
              <ul className="list-disc pl-6 text-secondary-700 dark:text-secondary-300 mb-6 space-y-2">
                <li>Active or anticipated legal proceedings</li>
                <li>Regulatory investigations (OAIC, state/territory health regulators)</li>
                <li>Court orders or subpoenas requiring preservation of evidence</li>
                <li>Professional standards investigations</li>
              </ul>
              <p className="text-secondary-700 dark:text-secondary-300">
                Legal holds are time-limited, documented, and reviewed quarterly. Upon release of a legal hold, standard deletion processes resume. You will be informed of any legal hold affecting your data where legally permissible.
              </p>
            </section>

            {/* Section 15 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                15. Contact Us
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300 mb-6">
                If you have any questions or complaints about our privacy practices, please contact:
              </p>
              <div className="bg-secondary-50 dark:bg-secondary-800 p-6 rounded-lg border border-secondary-200 dark:border-secondary-700">
                <p className="text-secondary-700 dark:text-secondary-300 mb-3">
                  <strong className="text-secondary-900 dark:text-white">📧 Email:</strong> privacy@notematic.com.au
                </p>
                <p className="text-secondary-700 dark:text-secondary-300">
                  <strong className="text-secondary-900 dark:text-white">📍 Address:</strong> Notematic Pty Ltd, 231 La Perouse street Red Hill ACT 2603
                </p>
              </div>
            </section>

            {/* Section 16 */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-secondary-900 dark:text-white mb-6 pb-3 border-b-2 border-primary-500">
                16. Updates to This Policy
              </h2>
              <p className="text-secondary-700 dark:text-secondary-300">
                We may update this Privacy Policy from time to time to reflect changes in technology, law, or our practices.
                The updated version will be published on our website and app with the "last updated" date.
              </p>
            </section>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

